import React from 'react';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2

import { TextField } from 'ui/components/TextField/TextField';
import { LocationsAsyncAutocomplete } from 'ui/components/Autocomplete/LocationsAsyncAutocomplete';
import { TransferOrderFormData } from 'services/transferOrders/types';
import { Location } from 'services/locations';
import { PermissionType } from 'services/permissions';

interface TransferOrderGeneralTabProps {
  formData: TransferOrderFormData;
  validationErrors: Record<string, string>;
  isDraft: boolean;
  onSourceLocationChange: (location: Location | null) => void;
  onDestinationLocationChange: (location: Location | null) => void;
  onTransferOrderNumberChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onTransferReasonChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

const TransferOrderGeneralTab: React.FC<TransferOrderGeneralTabProps> =
  React.memo(
    ({
      formData,
      validationErrors,
      isDraft,
      onSourceLocationChange,
      onDestinationLocationChange,
      onTransferOrderNumberChange,
      onTransferReasonChange,
    }) => {
      return (
        <Grid
          sx={{
            overflowY: 'scroll',
            maxHeight: '30%',
            padding: '16px',
          }}
          container
          columnSpacing="16px"
          columns={16}
          disableEqualOverflow
        >
          <Grid xs={4}>
            <LocationsAsyncAutocomplete
              label="Source Location *"
              value={formData.sourceLocationId}
              onChange={onSourceLocationChange}
              error={!!validationErrors.sourceLocationId}
              disabled={!isDraft}
              permissions={[PermissionType.TransferOrdersCreate]}
              dataQa="transfer-order-source-location"
            />
            {validationErrors.sourceLocationId && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {validationErrors.sourceLocationId}
              </Typography>
            )}
          </Grid>
          <Grid xs={4}>
            <LocationsAsyncAutocomplete
              label="Destination Location *"
              value={formData.destinationLocationId}
              onChange={onDestinationLocationChange}
              error={!!validationErrors.destinationLocationId}
              disabled={!isDraft}
              permissions={[PermissionType.TransferOrdersCreate]}
              dataQa="transfer-order-destination-location"
            />
            {validationErrors.destinationLocationId && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {validationErrors.destinationLocationId}
              </Typography>
            )}
          </Grid>
          <Grid xs={4}>
            <TextField
              className="redesign"
              variant="standard"
              label="Transfer order no."
              value={formData.transferOrderNumber || ''}
              onChange={onTransferOrderNumberChange}
              error={!!validationErrors.transferOrderNumber}
              helperText={validationErrors.transferOrderNumber}
              disabled={!isDraft}
              inputProps={{ maxLength: 50 }}
              permissions={[PermissionType.TransferOrdersCreate]}
              dataQa="transfer-order-number"
            />
          </Grid>
          <Grid xs={16}>
            <TextField
              className="redesign"
              variant="standard"
              label="Transfer Reason"
              value={formData.transferReason}
              onChange={onTransferReasonChange}
              error={!!validationErrors.transferReason}
              helperText={validationErrors.transferReason}
              disabled={!isDraft}
              inputProps={{ maxLength: 500 }}
              permissions={[PermissionType.TransferOrdersCreate]}
              dataQa="transfer-order-reason"
            />
          </Grid>
        </Grid>
      );
    }
  );

TransferOrderGeneralTab.displayName = 'TransferOrderGeneralTab';

export default TransferOrderGeneralTab;
